import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadsService } from "@/services/uploads.service";
import { cn, mediaUrl } from "@/lib/utils";
import { getErrorMessage } from "@/services/api";

/**
 * PhotoUploader - single or multiple image upload.
 *
 * Props:
 *  - value: string (single) or array of strings/objects (multiple)
 *  - onChange: (nextValue)=>void
 *  - multiple: bool
 *  - label: string
 *  - accept: string (default images)
 *  - testId: string (root data-testid)
 *  - photoWithMeta: if true, items are {url, category, caption}
 */
export function PhotoUploader({
    value,
    onChange,
    multiple = false,
    label,
    accept = "image/*",
    testId = "photo-uploader",
    photoWithMeta = false,
    disabled,
}) {
    const inputRef = useRef();
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    async function handleFiles(files) {
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const f of Array.from(files)) {
                const res = await uploadsService.upload(f);
                uploaded.push(res.url);
            }
            if (multiple) {
                const current = Array.isArray(value) ? value : [];
                const newItems = photoWithMeta
                    ? uploaded.map((url) => ({ url, category: "Geral", caption: "" }))
                    : uploaded;
                onChange([...current, ...newItems]);
            } else {
                onChange(uploaded[0]);
            }
        } catch (e) {
            toast.error(getErrorMessage(e, "Falha ao enviar arquivo."));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    }

    function onDrop(e) {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        handleFiles(e.dataTransfer.files);
    }

    function removeAt(index) {
        if (multiple && Array.isArray(value)) {
            const next = [...value];
            next.splice(index, 1);
            onChange(next);
        } else {
            onChange(null);
        }
    }

    // Single image display
    if (!multiple) {
        const currentUrl = typeof value === "string" ? value : value?.url;
        return (
            <div className="space-y-2" data-testid={testId}>
                {label && <label className="text-sm font-medium">{label}</label>}
                <div
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={cn(
                        "relative flex h-40 w-full items-center justify-center rounded-xl border-2 border-dashed bg-muted/40 transition-colors",
                        dragOver && "bg-accent",
                        disabled && "pointer-events-none opacity-60",
                    )}
                >
                    {currentUrl ? (
                        <div className="relative h-full w-full overflow-hidden rounded-xl">
                            <img
                                src={mediaUrl(currentUrl)}
                                alt=""
                                className="h-full w-full object-cover"
                            />
                            <button
                                type="button"
                                onClick={() => removeAt(0)}
                                className="absolute right-2 top-2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                                aria-label="Remover imagem"
                                data-testid={`${testId}-remove`}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <ImageIcon className="h-6 w-6" />
                            <div className="text-sm">Arraste ou selecione uma imagem</div>
                        </div>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        onChange={(e) => handleFiles(e.target.files)}
                        className="absolute inset-0 cursor-pointer opacity-0"
                        disabled={disabled || uploading}
                        data-testid={`${testId}-input`}
                    />
                </div>
                {uploading && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" /> Enviando…
                    </div>
                )}
            </div>
        );
    }

    // Multiple images grid
    const items = Array.isArray(value) ? value : [];
    return (
        <div className="space-y-3" data-testid={testId}>
            {label && <label className="text-sm font-medium">{label}</label>}
            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={cn(
                    "relative flex items-center justify-center rounded-xl border-2 border-dashed bg-muted/40 px-4 py-6",
                    dragOver && "bg-accent",
                    disabled && "pointer-events-none opacity-60",
                )}
            >
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Upload className="h-5 w-5" />
                    <div className="text-sm">Arraste ou selecione imagens</div>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        data-testid={`${testId}-button"`}
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Enviando…
                            </>
                        ) : (
                            <>Selecionar</>
                        )}
                    </Button>
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple
                    onChange={(e) => handleFiles(e.target.files)}
                    className="hidden"
                    disabled={disabled || uploading}
                    data-testid={`${testId}-input`}
                />
            </div>
            {items.length > 0 && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {items.map((it, i) => {
                        const url = typeof it === "string" ? it : it.url;
                        return (
                            <div key={`photo-${url || i}`} className="group relative overflow-hidden rounded-xl border bg-card">
                                <div className="aspect-[4/3] w-full">
                                    <img src={mediaUrl(url)} alt="" className="h-full w-full object-cover" />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeAt(i)}
                                    className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white opacity-0 transition-opacity hover:bg-black/70 group-hover:opacity-100"
                                    aria-label="Remover"
                                    data-testid={`${testId}-remove-${i}`}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
